import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import Loader from '../../components/loader/index';
import { LoadingCrossfade } from '../../components/loading-skeleton/index';
import { QueryKey } from '../../constants/index';
import { Company as CompanyHook, useScreenSize } from '../../hooks/index';
import { CompanyFilter, FilterVariant } from '../../types/company.types';
import MyCompanyCard from './components/company-card';
import EmptyCompanyCard from './components/empty-company-card';
import { NoCompaniesFilterEmptyState } from './components/no-active-companies-empty-state';
import { MyCompaniesPlaceholder } from './my-companies-loading-placeholders';

export type MyCompaniesItemsListProps = {
  numberOfFetchedItems?: number;
  selectedFilter: FilterVariant;
};

const MyCompaniesItemsList: FC<MyCompaniesItemsListProps> = ({
  selectedFilter,
  numberOfFetchedItems = 20,
}) => {
  const queryClient = useQueryClient();
  const { scrollableTarget } = useScreenSize();

  const { companies, fetchNextPage, hasNextPage, isLoading } = CompanyHook.useCompanies({
    filter: selectedFilter,
    numberOfFetchedItems,
  });

  const { selectedCompany, isLoading: isSelectedLoading } = CompanyHook.useSelected();

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_SELECTED_COMPANY],
    });
    queryClient.invalidateQueries({
      queryKey: [QueryKey.GET_COMPANIES],
    });
  }, [queryClient]);

  const isListEmpty = companies.length === 0;
  const isCreateCompanyEmptyState = isListEmpty && selectedFilter === 'all';
  const isFilterEmptyState =
    isListEmpty &&
    (selectedFilter === CompanyFilter.ACTIVE || selectedFilter === CompanyFilter.INACTIVE);
  const isEmptyStateShown = isCreateCompanyEmptyState || isFilterEmptyState;

  return (
    <LoadingCrossfade
      className={isEmptyStateShown ? 'flex flex-1 items-center justify-center' : undefined}
      isLoading={isSelectedLoading || isLoading || !companies}
      skeleton={<MyCompaniesPlaceholder />}
    >
      {() => {
        if (isCreateCompanyEmptyState) return <EmptyCompanyCard />;
        if (isFilterEmptyState) return <NoCompaniesFilterEmptyState filter={selectedFilter} />;

        return (
          <InfiniteScroll
            className="flex h-fit w-full flex-wrap items-stretch justify-start gap-4 p-4 max-lg:justify-center"
            dataLength={companies.length}
            hasMore={hasNextPage}
            loader={<Loader />}
            next={fetchNextPage}
            scrollableTarget={scrollableTarget}
          >
            {companies?.map((company) => (
              <MyCompanyCard
                key={company.id}
                {...company}
                invalidateQuery={invalidateQuery}
                isSelected={company.id === selectedCompany?.id}
              />
            ))}
          </InfiniteScroll>
        );
      }}
    </LoadingCrossfade>
  );
};

export default MyCompaniesItemsList;
